import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  type ValidationErrors,
  type ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import type { DeepReadonly } from '../../../server/util-type';
import { SettingsService, type ThemeString } from '../settings.service';
import {
  type OrgTaskWithUpdates,
  type TaskScheduleDialogComponentData,
  TaskService,
} from '../task.service';

@Component({
  selector: 'app-task-schedule-dialog',
  templateUrl: './task-schedule-dialog.component.html',
  styleUrls: ['./task-schedule-dialog.component.scss'],
  standalone: false,
})
export class TaskScheduleDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<TaskScheduleDialogComponent>);
  private readonly data: TaskScheduleDialogComponentData =
    inject(MAT_DIALOG_DATA);
  private taskService = inject(TaskService);
  private settingsService = inject(SettingsService);

  orgId: string;
  spaceId: string;
  taskId: string;

  title = '';
  scheduleText = '';

  task: DeepReadonly<OrgTaskWithUpdates>;

  hour12 = true;

  setDueTime = false;
  setStartTime = false;
  setEndTime = false;

  dueDateControl = new FormControl<Date | null>(null);
  dueTimeControl = new FormControl<string>('9:00', {
    validators: [Validators.required, timeMinuteValidator(this)],
    nonNullable: true,
  });

  startDateControl = new FormControl<Date | null>(null);
  startTimeControl = new FormControl<string>('9:00', {
    validators: [Validators.required, timeMinuteValidator(this)],
    nonNullable: true,
  });

  endDateControl = new FormControl<Date | null>(null);
  endTimeControl = new FormControl<string>('5:00 PM', {
    validators: [Validators.required, timeMinuteValidator(this)],
    nonNullable: true,
  });

  disableSubmit = false;

  themeClass: ThemeString; // We need the theme class here because the modals are outside of the root app div

  error = false;
  showEndBeforeError = false;
  showMoveToCurrentWarning = false;

  subscriptions = new Subscription();

  constructor() {
    this.orgId = this.data.orgId;
    this.spaceId = this.data.spaceId;
    this.taskId = this.data.taskId;
  }

  ngOnInit() {
    const { orgId, spaceId, taskId } = this;

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);

    const task = this.taskService.getTaskLocalSync({ orgId, spaceId, taskId });
    if (!task) {
      this.error = true;
      return;
    }
    this.task = task;

    this.title = this.task.title;

    const { hour12 } = this;

    // dueAt
    let dueTimeValue = this.task.dueAt;

    // If the timeValue has been removed, just set to current time
    if (dueTimeValue) {
      this.setDueTime = true;
    } else {
      // Default to 9am tomorrow
      const tomorrowsDate = dayjs().add(1, 'day').format().slice(0, 10);
      const tomorrowsDateTime = dayjs(tomorrowsDate + ' 09:00');
      dueTimeValue = tomorrowsDateTime.valueOf();
    }

    const dueDateTime = new Date(dueTimeValue);
    this.dueDateControl.setValue(dueDateTime);

    const initialDueTime = dueDateTime.toLocaleTimeString('en-US', {
      hour12,
      hour: 'numeric',
      minute: '2-digit',
    });
    this.dueTimeControl.setValue(initialDueTime);

    const dueDatePickerSubscribe = this.dueDateControl.valueChanges.subscribe(
      () => this.calculateErrors()
    );
    this.subscriptions.add(dueDatePickerSubscribe);

    const dueTimeControlSubscribe = this.dueTimeControl.valueChanges.subscribe(
      () => this.calculateErrors()
    );
    this.subscriptions.add(dueTimeControlSubscribe);

    // startAt
    let startTimeValue = this.task.schedule?.startAt;

    // If the timeValue has been removed, just set to current time
    if (startTimeValue) {
      this.setStartTime = true;
    } else {
      // Default to 9am tomorrow
      const tomorrowsDate = dayjs().add(1, 'day').format().slice(0, 10);
      const tomorrowsDateTime = dayjs(tomorrowsDate + ' 09:00');
      startTimeValue = tomorrowsDateTime.valueOf();
    }

    const startDateTime = new Date(startTimeValue);
    this.startDateControl.setValue(startDateTime);

    const initialStartTime = startDateTime.toLocaleTimeString('en-US', {
      hour12,
      hour: 'numeric',
      minute: '2-digit',
    });
    this.startTimeControl.setValue(initialStartTime);

    const startDatePickerSubscribe =
      this.startDateControl.valueChanges.subscribe(() =>
        this.calculateErrors()
      );
    this.subscriptions.add(startDatePickerSubscribe);

    const startTimeControlSubscribe =
      this.startTimeControl.valueChanges.subscribe(() =>
        this.calculateErrors()
      );
    this.subscriptions.add(startTimeControlSubscribe);

    // endAt
    if (this.task.schedule?.endAt) {
      this.setEndTime = true;
      const endDateTime = new Date(this.task.schedule.endAt);
      this.endDateControl.setValue(endDateTime);

      const initialEndTime = endDateTime.toLocaleTimeString('en-US', {
        hour12,
        hour: 'numeric',
        minute: '2-digit',
      });
      this.endTimeControl.setValue(initialEndTime);
    }

    const endDatePickerSubscribe = this.endDateControl.valueChanges.subscribe(
      () => this.calculateErrors()
    );
    this.subscriptions.add(endDatePickerSubscribe);

    const endTimeControlSubscribe = this.endTimeControl.valueChanges.subscribe(
      () => this.calculateErrors()
    );
    this.subscriptions.add(endTimeControlSubscribe);
  }

  private calculateErrors() {
    try {
      const dueDate = this.dueDateControl.value;
      if (!dueDate) throw new Error('Due date not set');

      // Determine if due will set currentAt to now
      const dueAt = this.convertTime(dueDate, this.dueTimeControl.value);
      this.showMoveToCurrentWarning =
        dueAt < dayjs().add(12, 'hours').valueOf();

      const startDate = this.startDateControl.value;
      if (!startDate) throw new Error('Start date not set');
      const scheduleStartAt = this.convertTime(
        startDate,
        this.startTimeControl.value
      );

      // Determine if start will set currentAt to now
      const startAt = this.convertTime(startDate, this.startTimeControl.value);
      this.showMoveToCurrentWarning =
        startAt < dayjs().add(12, 'hours').valueOf();

      const endDate = this.endDateControl.value;
      if (!endDate) throw new Error('End date not set');
      const scheduleEndAt = this.convertTime(
        endDate,
        this.endTimeControl.value
      );

      this.showEndBeforeError = scheduleEndAt < scheduleStartAt;
    } catch (error) {
      console.error(error);
    }

    this.disableSubmit = !!(
      (this.setDueTime &&
        (this.dueDateControl.errors || this.dueTimeControl.errors)) ||
      (this.setStartTime &&
        (this.startDateControl.errors || this.startTimeControl.errors)) ||
      (this.setEndTime &&
        (this.endDateControl.errors ||
          this.endTimeControl.errors ||
          this.showEndBeforeError))
    );
  }

  clickSetDue(checked: boolean) {
    this.setDueTime = checked;
    this.calculateErrors();
  }

  clickSetStart(checked: boolean) {
    this.setStartTime = checked;
    if (!this.setStartTime) this.setEndTime = false;
    this.calculateErrors();
  }

  clickSetEnd(checked: boolean) {
    this.setEndTime = checked;
    if (this.task.schedule?.endAt) {
      const endDateTime = new Date(this.task.schedule.endAt);
      this.endDateControl.setValue(endDateTime);
    } else {
      // Set to end of day on next day
      const startDate = this.startDateControl.value;
      const scheduleStartAt = !startDate
        ? Date.now()
        : this.convertTime(startDate, this.startTimeControl.value);

      const nextDayDate = dayjs(scheduleStartAt)
        .add(1, 'day')
        .format()
        .slice(0, 10);
      const tomorrowsDateTime = dayjs(nextDayDate + ' 17:00');
      const endTimeValue = tomorrowsDateTime.valueOf();
      const endDateTime = new Date(endTimeValue);
      this.endDateControl.setValue(endDateTime);
    }
    this.calculateErrors();
  }

  saveChanges() {
    const { orgId, spaceId, taskId } = this;
    const task =
      this.taskService.getTaskLocalSync({ orgId, spaceId, taskId }) ??
      this.task;

    let update:
      | {
          dueAt?: number | null;
          schedule?: { startAt: number; endAt?: number } | null;
          currentAt?: number;
        }
      | undefined = undefined;

    if (this.setDueTime) {
      const dueDate = this.dueDateControl.value;
      if (!dueDate) throw new Error('Due date not set');
      const dueAt = this.convertTime(dueDate, this.dueTimeControl.value);

      if (dueAt !== task.dueAt) {
        if (!update) update = {};
        update.dueAt = dueAt;
      }
    } else if (typeof task.dueAt === 'number') {
      if (!update) update = {};
      update.dueAt = null;
    }

    if (this.setStartTime) {
      let schedule: { startAt: number; endAt?: number } | null | undefined =
        undefined;

      const startDate = this.startDateControl.value;
      if (!startDate) throw new Error('Start date not set');
      schedule = {
        startAt: this.convertTime(startDate, this.startTimeControl.value),
      };

      if (this.setEndTime) {
        const endDate = this.endDateControl.value;
        if (!endDate) throw new Error('End date not set');
        schedule.endAt = this.convertTime(endDate, this.endTimeControl.value);
      }

      if (!_.isEqual(schedule, task.schedule)) {
        if (!update) update = {};
        update.schedule = schedule;

        // Move the
        if (
          !task.currentAt &&
          schedule.startAt < dayjs().add(12, 'hours').valueOf()
        ) {
          update.currentAt = Date.now();
          // TODO: set weight to the first position with either no schedule or
          // schedule.start is after this schedule.start
        }
      }
    } else if (task.schedule) {
      if (!update) update = {};
      update.schedule = null;
    }

    if (update) this.taskService.updateTask({ orgId, spaceId, taskId }, update);

    this.dialogRef.close();
  }

  private convertTime(date: Date, time: string) {
    const startDateString = dayjs(date).format().slice(0, 10) + ' ' + time;
    const dateObj = new Date(startDateString);
    const scheduleStartAt = dateObj.valueOf();
    return scheduleStartAt;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

function timeMinuteValidator(self): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const idRegEx = new RegExp(
      self.hour12
        ? /^(0?[1-9]|1[0-2]):[0-5][0-9](:[0-5][0-9])?(\s?[APap][mM])$/
        : /^(0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/
    );
    if (idRegEx.test(control.value)) return null;
    return { token: 'token failed validation' };
  };
}
